import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import { createRoot } from "react-dom/client";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { getInitials } from "../../../utils/utilityFunctions/stringUtility";
import AvatarName from "../../UniversalComponents/AvatarName";
import { getDDMonthYYYY } from "../../../utils/utilityFunctions/dateConvert";
import { ReactComponent as Calender } from "../../../assets/images/icons/Calender.svg";
import { ReactComponent as Recursion } from "../../../assets/images/icons/Recursion.svg";
import { ReactComponent as Activity } from "../../../assets/images/icons/menu.svg";
import { ReactComponent as Share } from "../../../assets/images/icons/Share.svg";
import { ReactComponent as Link } from "../../../assets/images/icons/Link.svg";
import { ReactComponent as RecurrenceIcon } from "../../../assets/images/TaskIcon.svg";
import { ReactComponent as Task } from "../../../assets/images/Task.svg";
import { ReactComponent as Lead } from "../../../assets/images/Lead.svg";
import { ReactComponent as Deal } from "../../../assets/images/Deal.svg";
import { ReactComponent as AddTaskCal } from "../../../assets/images/icons/AddTaskCal.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DatePickerComponent from "../../UniversalComponents/DatePickerComponent";
import zIndex from "@mui/material/styles/zIndex";
import { getDatePlusOne } from "../../../utils/utilityFunctions/dateUtility";
import { getUserDateTime } from "../../../utils/utilityFunctions/UTCFormat";
import FloatingElement from "../../UniversalComponents/FloatingElement";
import { px } from "framer-motion";

const locales = {
  "en-GB": require("date-fns/locale/en-GB"),
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const taskTypeIcons = {
  task: <Task />,
  lead: <Lead />,
  deal: <Deal />,
};
const priorityTextColor = {
  High: "#ED592E",
  Medium: "#782AEE",
  Low: "#37C015",
};

const getEventStyles = (priority, priority_colour_code, isSmallEvent) => {
  const fallbackColors = {
    High: "#F16136",
    Medium: "#103149",
    Low: "#9e00b8", // fallback color similar to what coming in API
    // High: "#ED592E",
    // Medium: "#782AEE",
    // Low: "#37C015",
  };

  const color = priority_colour_code || fallbackColors[priority];
  const STYLE_CONSTANTS = {
    High: {
      borderRadius: "10px 5px 5px 10px",
      background: `${color}15`,
      // boxShadow: `4px 0px 0px 0px ${hexToRgba(color, 0.4)} inset`,
      backdropFilter: "blur(1.5px)",
      color: `${color}`,
    },
    Medium: {
      borderRadius: "10px 5px 5px 10px",
      background: `${color}15`,
      // boxShadow: `4px 0px 0px 0px ${hexToRgba(color, 0.4)} inset`,
      backdropFilter: "blur(1.5px)",
      color: `${color}`,
    },
    Low: {
      borderRadius: "10px 5px 5px 10px",
      background: `${color}15`,
      // boxShadow: `4px 0px 0px 0px ${hexToRgba(color, 0.4)} inset`,
      backdropFilter: "blur(1.5px)",
      color: `${color}`,
    },
  };
  return STYLE_CONSTANTS[priority] || STYLE_CONSTANTS.Low;
};

function hexToRgba(hex, alpha = 1) {
  let col = hex.replace(/^#/, "");
  if (col.length === 3)
    col = col
      .split("")
      .map((c) => c + c)
      .join("");
  const num = parseInt(col, 16);
  const r = num >> 16;
  const g = (num >> 8) & 0x00ff;
  const b = num & 0x0000ff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const getPriorityPillStyles = (priority, priority_colour_code) => {
  const fallbackColors = {
    High: "F16136", // fallback color similar to what coming in API
    Medium: "#103149",
    Low: "#9e00b8",
    // High: "#ED592E",
    // Medium: "#782AEE",
    // Low: "#37C015",
  };

  const color = priority_colour_code || fallbackColors[priority];

  return {
    borderRadius: "999px",
    padding: "2px 8px",
    fontSize: "10px",
    fontWeight: 500,
    color: color,
    border: `1px solid ${color}`,
    backgroundColor: `${color}33`,
    textTransform: "capitalize",
  };
};

function transformTasksToEvents(taskList) {
  return (
    taskList
      // .filter((task) => task.show_on_calendar === 1)
      ?.map((task, idx) => {
        const {
          subject,
          status_colour_code,
          start_date,
          end_date,
          start_time = "00:00:00",
          end_time = "00:00:00",
          is_recurrence,
          assigned_to,
        } = task;

        const allDay = start_time === "00:00:00" && end_time === "00:00:00";
        const start = new Date(`${getUserDateTime(start_date, "toLocal")}`);
        const end = new Date(`${getUserDateTime(end_date || start_date, "toLocal")}`);

        return {
          ...task,
          title: subject || "Untitled Task",
          allDay,
          start,
          end,
          color: status_colour_code || "#3788d8",
          isRecurring: is_recurrence === 1,
          description: task.description || "",
          priority: task.priority || "",
          index: idx,
          taskData: { ...task, assigned_to },
        };
      })
  );
}

// Day header format
function getDaySuffix(day) {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

const CustomToolbar = ({ label, onNavigate, date, onView, views, view, onCreateTask }) => {
  const [selectedDate, setSelectedDate] = useState(date ? date.toISOString().substring(0, 10) : "");
  const [isDateVisible, setIsDateVisible] = useState(false);

  // Handler for when user picks a date
  const handleGoToDate = (value) => {
    if (value) {
      console.log(value, " I selected Date");
      const next = new Date(value);
      onNavigate("DATE", next);
    }
  };

  return (
    <div className="flex justify-between items-center mb-2">
      <div className="flex items-center justify-center gap-x-4">
        <div className="flex items-center gap-2 bg-[#F8F8F8] px-3 py-2 rounded-md">
          <button
            className="bg-primary-70 px-3 py-1 text-sm text-white rounded-md hover:bg-white hover:text-[#76818d] border border-gray-400"
            onClick={() => onNavigate("TODAY")}
          >
            Today
          </button>
          {views.map((v) => {
            return (
              <button
                key={v}
                onClick={() => onView(v)}
                className={`px-3 py-1 rounded-md text-sm border border-[#D2DAE1] 
               ${v === view ? "bg-primary hover:bg-primary-hover text-white hover:border-primary hover:text-primary" : "text-[#666666] hover:border-primary hover:text-primary"}`}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            );
          })}

          {/* <input
          type="date"
          className="ml-4 border-[#D2DAE1] rounded-md px-2.5 py-2 h-[38px] text-[#666666]"
          value={selectedDate}
          // onChange={(e) => setSelectedDate(e.target.value)}
          onChange={(e) => {
            setSelectedDate(e.target.value);
            handleGoToDate();
          }}
        /> */}
          {/* <button onClick={handleGoToDate} className="ml-2 px-2 py-1 border border-[#D2DAE1] rounded-md">
          Go
        </button> */}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onNavigate("PREV")}
            className="border border-[#D2DAE1] rounded-md w-8 h-8 flex items-center justify-center hover:border-primary group"
          >
            <ArrowBackIosNewIcon className="text-[#666666] group-hover:text-primary" style={{ fontSize: "14px" }} />
          </button>

          <div className="relative inline-block">
            <div
              onClick={() => setIsDateVisible(!isDateVisible)}
              className="font-semibold mx-2 text-lg text-primary min-w-[110px] text-center cursor-pointer"
            >
              {label}
            </div>

            {isDateVisible && (
              <div className="absolute left-1/3 z-50 mt-[-3px]">
                <FloatingElement onClose={() => setIsDateVisible(false)}>
                  <DatePickerComponent
                    value={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                      handleGoToDate(date);
                    }}
                    size="small"
                    className="w-full px-3 py-2 rounded-md border-[#d2d5d7]"
                    placeholder="Select date"
                    dateFormat="dd MMM yyyy"
                    placeholderText="DD MM YYYY"
                    showYearDropdown
                    autoOpen
                    showMonthDropdown
                    scrollableYearDropdown
                    border="hidden"
                    boxHidden={true}
                  />
                </FloatingElement>
              </div>
            )}
          </div>

          <button
            onClick={() => onNavigate("NEXT")}
            className="border border-[#D2DAE1] rounded-md w-8 h-8 flex items-center justify-center hover:border-primary group"
          >
            <ArrowForwardIosIcon className="text-[#666666] group-hover:text-primary" style={{ fontSize: "14px" }} />
          </button>
        </div>
      </div>
      <div className="group inline-flex items-center">
        <button
          className="text-primary group-hover:text-primary-70 font-medium flex items-center justify-start gap-x-1"
          onClick={onCreateTask}
        >
          <AddTaskCal className="fill-primary group-hover:fill-primary-70 " />
          Create Task
        </button>
      </div>
    </div>
  );
};

export default function RevampedTaskCalendar({ calendarData = [], openPopup, handleEditTask }) {
  const calendarRef = useRef();
  const safeData = Array.isArray(calendarData) ? calendarData : [];
  const events = useMemo(() => transformTasksToEvents(safeData), [safeData] || []);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());

  console.log(calendarData, events, "heelotasskk cal");
  // Ctrl+Wheel to zoom views
  useEffect(() => {
    const viewHierarchy = ["month", "week", "day"];
    let zoomTimeout = null;
    const onWheelZoom = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (zoomTimeout) return;
      zoomTimeout = setTimeout(() => {
        zoomTimeout = null;
      }, 300);
      const currentIndex = viewHierarchy.findIndex((v) => v === view);
      if (e.deltaY < 0 && currentIndex < viewHierarchy.length - 1) {
        setView(viewHierarchy[currentIndex + 1]);
      } else if (e.deltaY > 0 && currentIndex > 0) {
        setView(viewHierarchy[currentIndex - 1]);
      }
    };
    window.addEventListener("wheel", onWheelZoom, { passive: false });
    return () => window.removeEventListener("wheel", onWheelZoom);
  }, [view]);

  // Custom header format
  const formats = {
    // weekdayFormat: (date, culture, localizer) => {
    //   const weekday = date.toLocaleDateString("en-GB", { weekday: "short" });
    //   const day = date.getDate();
    //   const month = date.toLocaleDateString("en-GB", { month: "short" });
    //   const suffix = getDaySuffix(day);
    //   return "";
    //   return `${weekday}, ${day}${suffix} ${month}`;
    // },
    eventTimeRangeFormat: () => "",
  };

  const getBaseStyle = ({ isAllDay, isRecurring, priority, priority_colour_code, isSmallEvent }) => {
    const eventStyles = getEventStyles(priority, priority_colour_code, isSmallEvent) || {};
    return {
      ...eventStyles,
      backgroundColor: isAllDay ? "white" : isRecurring && (eventStyles.backgroundColor || "lightGray"),
      color: isAllDay ? "#1D4469" : "#ED592E",
      // border: isAllDay ? "1px solid #D2DAE1" : `1px solid ${priority_colour_code }`,
      borderRadius: "5px",
      padding: isSmallEvent ? "0px 5px" : "2px 6px",
      fontWeight: 500,
      fontSize: isSmallEvent ? "10px" : "14px",
      cursor: "pointer",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      gap: "2px",
      width: "100%",
      overflow: "hidden",
    };
  };

  const CustomEvent2 = ({ event, isAllDay, view }) => {
    const taskData = event.taskData || {};
    const isRecurring = !!taskData.is_recurrence;
    const priority = taskData?.priority || "Low";
    const { priority_colour_code } = taskData;
    const taskType = taskData?.category_id;

    console.log(view, "whatView");

    const isDayView = view === "week";
    const isSmallEvent = isDayView && event.start_time !== "00:00:00" && event.end_time !== "00:00:00";

    const baseStyle = getBaseStyle({ isAllDay, isRecurring, priority, priority_colour_code, isSmallEvent });

    return (
      <div
        style={{
          ...baseStyle,
          backgroundColor: `${priority_colour_code}15`,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: isSmallEvent ? "2px" : "8px",
          minWidth: 0,
          width: "100%",
          height: "100%",
        }}
      >
        {/* Left section: Priority & Subject */}
        <div style={{ display: "flex", gap: "2px", width: "40%" }}>
          {view === "timeGridDay" && (
            <div
              style={{
                fontSize: "10px",
                fontWeight: 400,
                borderRadius: "25px",
                backgroundColor: "red",
                border: priority_colour_code,
                borderWidth: "1px",
                padding: isSmallEvent ? "0px" : "3px 8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {priority}
            </div>
          )}
          <div
            style={{
              fontWeight: 500,
              color: "#666",
              fontSize: isSmallEvent ? "10px" : "12px",
              minWidth: 0,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              minWidth: 0,
              flex: 1,
              alignItems: "center",
              justifyContent: "start",
              display: "flex",
            }}
          >
            {taskData.subject}
          </div>
        </div>

        {/* Right section: Recurrence & Status */}
        <div style={{ display: "flex", alignContent: "flex-start", gap: "6px" }}>
          {isRecurring && <RecurrenceIcon />}
          {isRecurring && <div style={{ color: "#D2DAE1" }}>|</div>}
          <div
            style={{
              fontSize: "10px",
              fontWeight: 400,
              padding: "4px 8px",
              alignItems: "center",
              justifyContent: "center",
              color: `${taskData?.status_colour_code}` || "#008937",
              backgroundColor: `${taskData?.status_colour_code}33` || "#E6F4EB",
              display: "flex",
              borderRadius: "5px",
            }}
          >
            {taskData?.status}
          </div>
        </div>
      </div>
    );
  };

  const EventTooltipContent = ({ event }) => {
    const taskData = event;
    const isRecurring = taskData.is_recurrence;
    const isAllDay = event.allDay;
    const priority = taskData?.priority || "Low";
    const textColor =
      {
        High: "#F16136",
        Medium: "#103149",
        Low: "#9e00b8",
        // High: "#ED592E",
        // Medium: "#782AEE",
        // Low: "#37C015",
      }[priority] || "#37C015";
    const { priority_colour_code } = taskData;
    const startTime = event.start
      ? new Date(event.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "";
    const endTime = event.end ? new Date(event.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
    const timeString = `${startTime} - ${endTime}`;
    const taskType = taskData?.category_id;
    const icon =
      {
        task: <Task />,
        lead: <Lead />,
        deal: <Deal />,
      }[taskType] || null;
    const avatarColors = ["#FABF26", "#37C015", "#BE4A25"];
    const getSequentialColor = (index) => {
      return avatarColors[index % avatarColors.length];
    };

    const eventIndex = taskData.index || 0;

    // const avatarBgColor = getSequentialColor(eventIndex);
    // const assignedTo =
    const baseStyle = {
      backgroundColor: getEventStyles(priority, priority_colour_code) || "white",
      color: isAllDay ? "#1D4469" : "#ED592E",
      // border: isAllDay ? "1px solid #D2DAE1" : "1px solid blue",
      borderRadius: "4px",
      padding: "2px 6px",
      fontWeight: 500,
      fontSize: "14px",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      gap: "5px",
      overflow: "hidden",
      width: "100%",
      minWidth: "300px",
      zIndex: "9999",
    };
    return (
      <div style={baseStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div
            style={{
              fontSize: "10px",
              fontWeight: "400",
              color: textColor,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "3px 5px",
            }}
          >
            {/* {taskType} */}
            <div className="relative inline-flex items-center py-1">
              {icon}
              <span style={{ color: "#868686", padding: "2px", textTransform: "capitalize" }}>{taskType}</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div style={getPriorityPillStyles(priority, priority_colour_code)}>{priority}</div>
          </div>
        </div>
        <div style={{ fontSize: "12px" }} className="ml-1 flex flex-col gap-1">
          <div style={{ fontWeight: "500", color: textColor, textTransform: "capitalize" }}>{taskData.title}</div>
          <div style={{ fontWeight: "500", color: "#878787" }}>{timeString}</div>
        </div>

        <div className="w-full border"></div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          {/* avatar - left bottom */}
          <div className="flex py-1">
            {taskData?.assigned_to?.length > 1 ? (
              <div className="flex  items-center ml-2">
                {taskData?.assigned_to.slice(0, 3).map((user, i) => {
                  const initials = getInitials(user.name);
                  return (
                    <div key={i} className="relative ml-[-5px] border rounded-full border-white">
                      <AvatarName name={initials} size="medium" bgColor={user.c_key} textColor="ffffff" />
                    </div>
                  );
                })}
                <div className="ml-[-5px]">
                  <AvatarName
                    name={`+${taskData.assigned_to.length}`}
                    size="medium"
                    bgColor="#D8EEF7"
                    textColor="#000000"
                  />
                </div>
              </div>
            ) : (
              taskData?.assigned_to.map((user, i) => {
                const initials = getInitials(user.name);
                return (
                  <div key={i} className="flex items-center ml-1 gap-x-2 flex-wrap capitalize">
                    <AvatarName name={initials} size="medium" bgColor={user.c_key} textColor="FFFFFF" />
                    {user.name}
                  </div>
                );
              })
            )}
          </div>

          {/* status & other icons- right bottom */}
          <div className="flex items-center gap-2">
            {Boolean(isRecurring) && (
              <div
                style={{
                  borderRadius: "5px",
                  backgroundColor: "rgba(255, 255, 255, 0.60)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  height: "24px",
                  width: "24px",
                }}
              >
                <div className="flex">
                  <RecurrenceIcon />
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Activity className=" fill-gray-400 hover:fill-blue-500 transition-all duration-100" />
              <Share className=" fill-gray-400 hover:fill-blue-500 transition-all duration-100" />
              <Link className=" fill-gray-400 hover:fill-blue-500 transition-all duration-100" />
            </div>
            {false && (
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "400",
                  display: "flex",
                  // height: "22px",
                  margin: "3px 0px",
                  padding: "3px",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#008937",
                  backgroundColor: "#E6F4EB",
                  border: "0.5px #008937 solid",
                  borderRadius: "3px",
                }}
              >
                {taskData?.status}
              </div>
            )}
          </div>
        </div>
        {/* {isRecurring && (
            <span title="Recurring Task" style={{ marginLeft: "4px" }}>
              🔁
            </span>
          )} */}
      </div>
    );
  };

  // const CustomEvent = ({ event }) => (
  //   <Tippy content={<EventTooltipContent event={event} visible={true} trigger="manual"  />} theme="light" placement="auto" interactive zIndex={20000}>
  //     <div style={{ cursor: "pointer", minWidth: "fit-content" }}>
  //       {/* Your event rendering logic or custom event display */}
  //       <CustomEvent2 event={event} />
  //     </div>
  //   </Tippy>
  // );

  const CustomEvent = ({ event }) => {
    //    const stopRootCloseCapture = React.useCallback((el) => {
    //   if (!el) return;
    //   // Block RBC's document-level root-close (capture phase)
    //   ["pointerdown", "mousedown", "click", "touchstart"].forEach((type) => {
    //     el.addEventListener(type, (e) => e.stopPropagation(), true);
    //   });
    // }, []);

    return (
      <>
        {console.log(event, "eventCustom")}

        <Tippy
          content={
            <div
              onClick={(e) => {
                const taskData = event.taskData;
                handleEditTask?.(null, taskData, false, false, taskData?.is_recurrence === 1);
                e.stopPropagation();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <EventTooltipContent event={event} />
            </div>
          }
          theme="light"
          // placement="auto"
          placement="right-start"
          interactive
          // zIndex={20000}
          appendTo={() => document.getElementById("portal-root")}
          //    appendTo={(refEl) => {
          // // try nearest open overlay
          //     const overlay = refEl?.closest?.('.rbc-overlay')
          //       || document.querySelector('.rbc-overlay');
          //     return overlay || document.getElementById("portal-root");
          //   }}

          popperOptions={{
            strategy: "fixed", // stable on scroll
            modifiers: [
              { name: "preventOverflow", options: { boundary: document.body, altAxis: true } },
              { name: "flip", options: { fallbackPlacements: ["left-start", "right", "left"] } }, // lateral only
              { name: "computeStyles", options: { adaptive: false } },
            ],
          }}
          // onMount={(instance) => stopRootCloseCapture(instance.popper)} // keep RBC popup open
          //  offset={[8, 0]}
          // visible={true}       // Always show
          // trigger="manual"     // Prevent hover/focus triggers
        >
          <div style={{ cursor: "pointer", height: "100%" }}>
            <CustomEvent2 event={event} isAllDay={event?.allDay} view={view} />
          </div>
        </Tippy>
      </>
    );
  };

  return (
    <div
      className="App 
    [&_.rbc-calendar]:rounded-[5px] pb-5 
    [&_.rbc-event]:!bg-transparent 
    [&_.rbc-event-content]:!bg-transparent
    [&_.rbc-show-more]:px-2
  "
    >
      <Calendar
        localizer={localizer}
        ref={calendarRef}
        events={events}
        defaultView={view}
        view={view}
        onView={setView}
        date={date}
        onNavigate={setDate}
        components={{
          // components: {
          //   toolbar: (props) => <CustomToolbar {...props} onCreateTask={openPopup} />,
          // },
          toolbar: (props) => <CustomToolbar {...props} onCreateTask={openPopup} />,
          event: CustomEvent,
        }}
        // tooltipAccessor={() => <div>Hello</div>}
        tooltipAccessor={null}
        popup={true}
        popupOffset={{ x: 16, y: 12 }}
        selectable={true}
        onSelectSlot={(slotInfo) => {
          console.log(slotInfo, "slotInfo aakash");
          openPopup({ start: { value: slotInfo.start }, end: { value: slotInfo.end } });
        }}
        onSelectEvent={(event) => {
          const taskData = event.taskData;
          handleEditTask?.(null, taskData, false, false, taskData?.is_recurrence === 1);
        }}
        startAccessor="start"
        endAccessor="end"
        allDayAccessor="allDay"
        style={{ height: "100%", minHeight: 700 }}
        messages={{
          week: "Week",
          work_week: "Work Week",
          day: "Day",
          month: "Month",
          previous: "<",
          next: ">",
          today: "Today",
          allDay: "🕐",
        }}
        formats={formats}
        eventPropGetter={(event, start, end, isSelected) => {
          // console.log(event, "timeViewwww");

          const isDayView = view === "week"; // your view state
          if (isDayView && event.start_time !== "00:00:00" && event.end_time !== "00:00:00") {
            return {
              style: {
                padding: "0px",
                fontSize: "8px",
                boxShadow: "none",
              },
            };
          }
          return {
            // style: {
            //   display: "none"
            // }
          }; // default
        }}
      />
    </div>
  );
}
